import React, { useState, useMemo, useEffect } from 'react';
import moment from 'moment';
import styles from './dayCalendar.module.css';

const DOW_LABELS = ['MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT', 'SUN'];
const MONTH_NAMES = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
];
const QUARTERS = [
  { quarter: 1, label: 'Q1', subtitle: 'Jan-Mar' },
  { quarter: 2, label: 'Q2', subtitle: 'Apr-Jun' },
  { quarter: 3, label: 'Q3', subtitle: 'Jul-Sep' },
  { quarter: 4, label: 'Q4', subtitle: 'Oct-Dec' },
];
const MIN_YEAR = 2017;
const MAX_YEAR = new Date().getFullYear() + 2;

interface DayCalendarProps {
  selectedDate: string;        // YYYY-MM-DD
  onChange: (date: string) => void;
  rangeOtherEnd?: string;      // YYYY-MM-DD — the other boundary for range highlight
  role: 'start' | 'end';
  minDate?: moment.Moment;
  maxDate?: moment.Moment;
  mode?: 'day' | 'quarter';    // 'day' for daily/weekly/monthly, 'quarter' for quarterly
  disableDropdowns?: boolean;  // disables month/year navigation dropdowns (e.g. for daily granularity)
}

interface DayCell {
  day: number;
  dateStr: string;             // YYYY-MM-DD
  isCurrentMonth: boolean;
  isToday: boolean;
  isSelected: boolean;
  isInRange: boolean;
  isDisabled: boolean;
  isRangeStart: boolean;
  isRangeEnd: boolean;
}

const DayCalendar: React.FC<DayCalendarProps> = ({
  selectedDate,
  onChange,
  rangeOtherEnd,
  role,
  minDate,
  maxDate,
  mode = 'day',
  disableDropdowns = false,
}) => {
  const selected = moment(selectedDate);
  const [viewYear, setViewYear] = useState(selected.isValid() ? selected.year() : moment().year());
  const [viewMonth, setViewMonth] = useState(selected.isValid() ? selected.month() : moment().month());
  const [showMonthDropdown, setShowMonthDropdown] = useState(false);
  const [showYearDropdown, setShowYearDropdown] = useState(false);

  // Keep the visible month/year in sync when the selected date changes
  // (e.g. choosing a Date Range preset like "Last Month" or "This Year").
  // Without this, the calendar stays on the initial month and the
  // selected range is never shown/highlighted.
  useEffect(() => {
    if (selected.isValid()) {
      setViewYear(selected.year());
      setViewMonth(selected.month());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDate]);

  const todayStr = moment().format('YYYY-MM-DD');
  const isQuarterly = mode === 'quarter';

  // Compute the range boundaries for highlighting
  const rangeStart = useMemo(() => {
    if (!rangeOtherEnd) return null;
    const a = moment(selectedDate);
    const b = moment(rangeOtherEnd);
    return moment.min(a, b).format('YYYY-MM-DD');
  }, [selectedDate, rangeOtherEnd]);

  const rangeEnd = useMemo(() => {
    if (!rangeOtherEnd) return null;
    const a = moment(selectedDate);
    const b = moment(rangeOtherEnd);
    return moment.max(a, b).format('YYYY-MM-DD');
  }, [selectedDate, rangeOtherEnd]);

  // Build day cells for the current view month, including leading/trailing days
  const cells: DayCell[] = useMemo(() => {
    const firstOfMonth = moment({ year: viewYear, month: viewMonth, day: 1 });
    const daysInMonth = firstOfMonth.daysInMonth();
    const startOffset = firstOfMonth.isoWeekday() - 1; // 0=Mon

    const result: DayCell[] = [];

    function isDateDisabled(dateStr: string): boolean {
      if (minDate && dateStr < minDate.format('YYYY-MM-DD')) return true;
      if (maxDate && dateStr > maxDate.format('YYYY-MM-DD')) return true;
      return false;
    }

    // Leading days from previous month
    if (startOffset > 0) {
      const prevMonth = firstOfMonth.clone().subtract(1, 'month');
      const prevDays = prevMonth.daysInMonth();
      for (let i = startOffset - 1; i >= 0; i--) {
        const day = prevDays - i;
        const dateStr = prevMonth.clone().date(day).format('YYYY-MM-DD');
        result.push({
          day,
          dateStr,
          isCurrentMonth: false,
          isToday: dateStr === todayStr,
          isSelected: dateStr === selectedDate,
          isInRange: rangeStart && rangeEnd ? dateStr >= rangeStart && dateStr <= rangeEnd : false,
          isDisabled: isDateDisabled(dateStr),
          isRangeStart: dateStr === rangeStart,
          isRangeEnd: dateStr === rangeEnd,
        });
      }
    }

    // Current month days
    for (let day = 1; day <= daysInMonth; day++) {
      const dateStr = firstOfMonth.clone().date(day).format('YYYY-MM-DD');
      result.push({
        day,
        dateStr,
        isCurrentMonth: true,
        isToday: dateStr === todayStr,
        isSelected: dateStr === selectedDate,
        isInRange: rangeStart && rangeEnd ? dateStr >= rangeStart && dateStr <= rangeEnd : false,
        isDisabled: isDateDisabled(dateStr),
        isRangeStart: dateStr === rangeStart,
        isRangeEnd: dateStr === rangeEnd,
      });
    }

    // Trailing days from next month (fill to complete last row, always 6 rows × 7 = 42 cells)
    const remaining = 42 - result.length;
    const nextMonth = firstOfMonth.clone().add(1, 'month');
    for (let day = 1; day <= remaining; day++) {
      const dateStr = nextMonth.clone().date(day).format('YYYY-MM-DD');
      result.push({
        day,
        dateStr,
        isCurrentMonth: false,
        isToday: dateStr === todayStr,
        isSelected: dateStr === selectedDate,
        isInRange: rangeStart && rangeEnd ? dateStr >= rangeStart && dateStr <= rangeEnd : false,
        isDisabled: isDateDisabled(dateStr),
        isRangeStart: dateStr === rangeStart,
        isRangeEnd: dateStr === rangeEnd,
      });
    }

    return result;
  }, [viewYear, viewMonth, selectedDate, rangeOtherEnd, todayStr, rangeStart, rangeEnd, minDate, maxDate]);

  const handleDayClick = (cell: DayCell) => {
    if (cell.isDisabled) return;
    onChange(cell.dateStr);
    // If clicked day is in a different month, navigate to that month
    const clicked = moment(cell.dateStr);
    if (clicked.month() !== viewMonth || clicked.year() !== viewYear) {
      setViewMonth(clicked.month());
      setViewYear(clicked.year());
    }
  };

  // Quarter handling for quarterly mode
  const selectedQuarter = selected.isValid() ? selected.quarter() : moment().quarter();
  const selectedYear = selected.isValid() ? selected.year() : moment().year();

  const handleQuarterClick = (quarter: number) => {
    const quarterMoment = moment({ year: viewYear, month: (quarter - 1) * 3, day: 1 });
    if (minDate && quarterMoment.isBefore(minDate, 'quarter')) return;
    if (maxDate && quarterMoment.isAfter(maxDate, 'quarter')) return;
    
    // For start date: use first day of quarter; for end date: use last day of quarter
    const newDate = role === 'end' 
      ? quarterMoment.endOf('quarter').format('YYYY-MM-DD')
      : quarterMoment.startOf('quarter').format('YYYY-MM-DD');
    onChange(newDate);
  };

  const isQuarterSelected = (quarter: number) => {
    return selectedQuarter === quarter && selectedYear === viewYear;
  };

  const isQuarterInRange = (quarter: number) => {
    if (!rangeStart || !rangeEnd) return false;
    const quarterStart = moment({ year: viewYear, month: (quarter - 1) * 3, day: 1 });
    const rangeStartMoment = moment(rangeStart);
    const rangeEndMoment = moment(rangeEnd);
    return quarterStart.isSameOrAfter(rangeStartMoment, 'quarter') && 
           quarterStart.isSameOrBefore(rangeEndMoment, 'quarter');
  };

  const isQuarterDisabled = (quarter: number) => {
    const quarterStart = moment({ year: viewYear, month: (quarter - 1) * 3, day: 1 });
    if (minDate && quarterStart.isBefore(minDate, 'quarter')) return true;
    if (maxDate && quarterStart.isAfter(maxDate, 'quarter')) return true;
    return false;
  };

  const handleMonthSelect = (monthIndex: number) => {
    setViewMonth(monthIndex);
    setShowMonthDropdown(false);
  };

  const handleYearSelect = (year: number) => {
    setViewYear(year);
    setShowYearDropdown(false);
  };

  const years = Array.from(
    { length: MAX_YEAR - MIN_YEAR + 1 },
    (_, i) => MIN_YEAR + i
  );

  // Format the date input display
  const inputDisplay = selected.isValid()
    ? isQuarterly
      ? `Q${selectedQuarter}, ${selectedYear}`
      : selected.format('MMM DD, YYYY')
    : '';

  return (
    <div className={styles.calendar}>
      
      {/* Date input (read-only) */}
      <div className={styles.dateInput}>
        {inputDisplay}
      </div>

      {/* Month + Year dropdowns row (Year only for quarterly) */}
      <div className={styles.dropdownRow}>
        {/* Month dropdown - hide in quarterly mode */}
        {!isQuarterly && (
          <div className={styles.dropdownWrapper}>
            <button
              className={`${styles.dropdownBtn} ${disableDropdowns ? styles.dropdownBtnDisabled : ''}`}
              onClick={() => {
                if (disableDropdowns) return;
                setShowMonthDropdown(!showMonthDropdown);
                setShowYearDropdown(false);
              }}
              type="button"
              disabled={disableDropdowns}
            >
              {MONTH_NAMES[viewMonth]}
              <span className={styles.dropdownChevron} />
            </button>
            {showMonthDropdown && (
              <div className={styles.dropdownMenu}>
                {MONTH_NAMES.map((name, idx) => (
                  <div
                    key={name}
                    className={`${styles.dropdownItem} ${viewMonth === idx ? styles.dropdownItemActive : ''}`}
                    onClick={() => handleMonthSelect(idx)}
                  >
                    {name}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Year dropdown */}
        <div className={styles.dropdownWrapper}>
          <button
            className={`${styles.dropdownBtn} ${disableDropdowns ? styles.dropdownBtnDisabled : ''}`}
            onClick={() => {
              if (disableDropdowns) return;
              setShowYearDropdown(!showYearDropdown);
              setShowMonthDropdown(false);
            }}
            type="button"
            disabled={disableDropdowns}
          >
            {viewYear}
            <span className={styles.dropdownChevron} />
          </button>
          {showYearDropdown && (
            <div className={`${styles.dropdownMenu} ${styles.yearMenu}`}>
              {years.map(year => (
                <div
                  key={year}
                  className={`${styles.dropdownItem} ${viewYear === year ? styles.dropdownItemActive : ''}`}
                  onClick={() => handleYearSelect(year)}
                >
                  {year}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Quarter grid for quarterly mode */}
      {isQuarterly && (
        <div className={styles.quarterGrid}>
          {QUARTERS.map(({ quarter, label, subtitle }) => {
            const isSelected = isQuarterSelected(quarter);
            const isInRange = isQuarterInRange(quarter);
            const isDisabled = isQuarterDisabled(quarter);
            const classNames = [
              styles.quarterCard,
              isSelected ? styles.selected : '',
              isInRange && !isSelected ? styles.inRange : '',
              isDisabled ? styles.disabled : '',
            ].filter(Boolean).join(' ');
            
            return (
              <button
                key={quarter}
                type="button"
                className={classNames}
                onClick={() => handleQuarterClick(quarter)}
                disabled={isDisabled}
              >
                <span className={styles.quarterLabel}>{label}</span>
                <span className={styles.quarterSubtitle}>{subtitle}</span>
              </button>
            );
          })}
        </div>
      )}

      {/* Day-of-week header and day grid - hide in quarterly mode */}
      {!isQuarterly && (
        <div className={styles.dayGrid}>
          {DOW_LABELS.map(d => (
            <div key={d} className={styles.dowLabel}>{d}</div>
          ))}

          {/* Day cells */}
          {cells.map((cell, idx) => {
            const classNames = [
              styles.dayCell,
              !cell.isCurrentMonth ? styles.outsideMonth : '',
              cell.isToday ? styles.today : '',
              cell.isSelected ? styles.selected : '',
              cell.isInRange && !cell.isSelected ? styles.inRange : '',
              cell.isDisabled ? styles.disabled : '',
              cell.isRangeStart ? styles.rangeStart : '',
              cell.isRangeEnd ? styles.rangeEnd : '',
          ].filter(Boolean).join(' ');

          return (
            <div
              key={`${cell.dateStr}-${idx}`}
              className={classNames}
              onClick={() => handleDayClick(cell)}
              role="button"
              tabIndex={cell.isDisabled ? -1 : 0}
              aria-label={cell.dateStr}
              onKeyDown={e => e.key === 'Enter' && handleDayClick(cell)}
            >
              {cell.day}
            </div>
          );
        })}
        </div>
      )}
    </div>
  );
};

export default DayCalendar;
